import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { BunkerPricesService } from '@/services/bunkerPricesService';
import type {
  BunkerPricesState,
  HistoricalPeriod,
  GroupedBunkerPrices,
} from '@/types/bunkerPrices.types';

interface BunkerPricesStore extends BunkerPricesState {
  // Actions
  fetchBunkerPrices: () => Promise<void>;
  fetchBunkerPriceHistory: (portId: number, period: HistoricalPeriod) => Promise<void>;
  setSelectedPort: (portId: number | null) => void;
  setSelectedPeriod: (period: HistoricalPeriod) => void;
  refreshBunkerPrices: () => Promise<void>;
  getGroupedPrices: () => GroupedBunkerPrices[];
  historyLastUpdated: Date | null;
}

export const useBunkerPricesStore = create<BunkerPricesStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      prices: [],
      history: [],
      selectedPort: null,
      selectedPeriod: 0, // Daily
      loading: false,
      historyLoading: false,
      error: null,
      historyError: null,
      historyLastUpdated: null,

      // Actions
      fetchBunkerPrices: async () => {
        set({ loading: true, error: null });

        try {
          const prices = await BunkerPricesService.getBunkerPrices();
          set({ prices, loading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch bunker prices';
          set({ error: errorMessage, loading: false });
        }
      },

      fetchBunkerPriceHistory: async (portId: number, period: HistoricalPeriod) => {
        set({ historyLoading: true, historyError: null });

        try {
          const history = await BunkerPricesService.getBunkerPricesHistory(portId, period);
          set({
            history,
            historyLoading: false,
            selectedPort: portId,
            selectedPeriod: period,
            historyLastUpdated: new Date(),
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch bunker price history';
          set({ historyError: errorMessage, historyLoading: false });
        }
      },

      setSelectedPort: (portId: number | null) => {
        set({ selectedPort: portId });
      },

      setSelectedPeriod: (period: HistoricalPeriod) => {
        set({ selectedPeriod: period });
      },

      refreshBunkerPrices: async () => {
        set({ loading: true, error: null });

        try {
          const prices = await BunkerPricesService.getBunkerPrices();
          set({ prices, loading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to refresh bunker prices';
          set({ error: errorMessage, loading: false });
        }
      },

      getGroupedPrices: (): GroupedBunkerPrices[] => {
        const { prices } = get();
        const grouped: Record<string, GroupedBunkerPrices> = {};

        prices.forEach((price) => {
          if (!grouped[price.portName]) {
            grouped[price.portName] = {
              portName: price.portName,
              portId: price.portId,
              prices: [],
            };
          }
          grouped[price.portName].prices.push(price);
        });

        // Sort prices within each port by order field
        Object.values(grouped).forEach((group) => {
          group.prices.sort((a, b) => a.order.localeCompare(b.order));
        });

        return Object.values(grouped);
      },
    }),
    { name: 'BunkerPricesStore' }
  )
);
